// The list of trusted devices: one append-only chain of changes per owner,
// each version signed by an approver of the version before it. The stores
// (the Vault and Cloud) keep the chain as it is and hand it out; every device
// checks it here, so a store can neither add a device nor drop a removal
// without it showing.
//
// Promise:
// - Keys are P-256 ECDSA (ES256), written as base64url of the uncompressed
//   point (65 bytes); signatures are base64url of r||s (64 bytes). A member's
//   id is the hex of the first 16 bytes of the SHA-256 of its approve key (of
//   its instruct key when it has no approve key: a receiver).
// - An entry is signed over the canonical JSON (sorted keys, no spaces) of
//   everything but `sig`; its hash is the hex SHA-256 of the canonical JSON of
//   the whole entry.
// - `verifyChain` takes a whole chain (n = 1, 2, ...) and accepts it only when
//   every entry is of the same list, numbered one after another, points at
//   the hash of the one before (`prev`), and is signed by an approver of the
//   version before it (the genesis by its own member). An add brings a member
//   not in the list whose id matches its keys; a remove takes one that is in
//   and never the last approver. Given the head a device accepted before, a
//   chain shorter than it (rolled back) or with another hash at its number
//   (forked) is refused.
// - The approval handshake's secrets come from the code's secret through
//   PBKDF2 (so what a store sees of a room cannot be turned back into the
//   code by trying every code), and each side proves the secret with an HMAC
//   over everything both sides said. A Cloud account the approver hands over
//   with the list carries the same HMAC (`handoffMac`), and its ticket is
//   redeemed with the new device's instruct key (`handoffProofBytes`). A
//   device signed in to Cloud by a code names its member there with the same
//   key (`memberProofBytes`).
import { z } from "zod";

/**
 * A key as JWK (the EC fields this file uses). Declared here so the module
 * reads the same with Node's types and a browser's: the code uses the
 * global WebCrypto either way.
 */
export type JsonWebKey = {
  kty?: string;
  crv?: string;
  x?: string;
  y?: string;
  d?: string;
  ext?: boolean;
  key_ops?: string[];
};

export const TRUST_VERSION = 1;
/** Bytes of the secret in an approval code. */
export const APPROVAL_SECRET_BYTES = 6;
/** How long an approval code lives. */
export const APPROVAL_TTL_MS = 10 * 60 * 1000;
const APPROVAL_KDF_ITERATIONS = 200_000;
const APPROVAL_KDF_SALT = "trameria trust approval v1";
const MAX_NAME = 100;

export type TrustRole = "approver" | "receiver";
export type TrustKind = "phone" | "pc" | "cloud";

export interface TrustMember {
  id: string;
  /** null for a receiver (it approves nothing). */
  approveKey: string | null;
  instructKey: string;
  role: TrustRole;
  name: string;
  kind: TrustKind;
  /**
   * Whose agents take this member's "any" instructions: "any" every agent of
   * the list (its instruct key no other process of its device can use: a
   * mac's keychain), "self" its own PC's agent only. Absent: a phone's are
   * every agent's, any other's its own.
   */
  instructs?: "any" | "self";
}

export type TrustChange =
  | { genesis: TrustMember }
  | { add: TrustMember }
  | { remove: string };

export interface TrustEntry {
  v: number;
  list: string;
  n: number;
  prev: string | null;
  change: TrustChange;
  by: string;
  /** The signing device's clock (shown only, never used for order). */
  at: string;
  sig: string;
}

export type UnsignedTrustEntry = Omit<TrustEntry, "sig">;

/** A version of a list as a device remembers it. */
export interface TrustHead {
  list: string;
  n: number;
  hash: string;
}

export interface TrustState extends TrustHead {
  members: TrustMember[];
}

const b64u = /^[A-Za-z0-9_-]+$/;
const hex32 = /^[0-9a-f]{32}$/;
const hex64 = /^[0-9a-f]{64}$/;

export const trustMemberShape = z.strictObject({
  id: z.string().regex(hex32),
  approveKey: z.string().regex(b64u).length(87).nullable(),
  instructKey: z.string().regex(b64u).length(87),
  role: z.enum(["approver", "receiver"]),
  name: z.string().min(1).max(MAX_NAME),
  kind: z.enum(["phone", "pc", "cloud"]),
  instructs: z.enum(["any", "self"]).optional(),
});

export const trustEntryShape = z.strictObject({
  v: z.literal(TRUST_VERSION),
  list: z.string().regex(hex32),
  n: z.number().int().min(1),
  prev: z.string().regex(hex64).nullable(),
  change: z.union([
    z.strictObject({ genesis: trustMemberShape }),
    z.strictObject({ add: trustMemberShape }),
    z.strictObject({ remove: z.string().regex(hex32) }),
  ]),
  by: z.string().regex(hex32),
  at: z.string().min(1).max(40),
  sig: z.string().regex(b64u).length(86),
});

export const trustHeadShape = z.strictObject({
  list: z.string().regex(hex32),
  n: z.number().int().min(1),
  hash: z.string().regex(hex64),
});

/** JSON with sorted keys and no spaces (what is signed and hashed). `undefined` is dropped. */
export function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  if (value !== null && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(",")}}`;
  }
  return JSON.stringify(value);
}

const subtle = () => globalThis.crypto.subtle;
const utf8 = (text: string) => new TextEncoder().encode(text);
/** The bytes as a plain ArrayBuffer (what WebCrypto takes under every lib's types). */
const ab = (b: Uint8Array): ArrayBuffer =>
  b.buffer.slice(b.byteOffset, b.byteOffset + b.byteLength) as ArrayBuffer;

export function toBase64Url(bytes: Uint8Array): string {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function fromBase64Url(text: string): Uint8Array {
  const bin = atob(
    text.replace(/-/g, "+").replace(/_/g, "/") +
      "===".slice((text.length + 3) % 4),
  );
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export const toHex = (bytes: Uint8Array) =>
  Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");

export async function sha256(bytes: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await subtle().digest("SHA-256", ab(bytes)));
}

export const randomBytes = (n: number) =>
  globalThis.crypto.getRandomValues(new Uint8Array(n));

/** A new list's id (random). */
export const newListId = () => toHex(randomBytes(16));

/** A member's id: its approve key's SHA-256 (its instruct key's for a receiver), first 16 bytes. */
export async function memberIdOf(
  keys: Pick<TrustMember, "approveKey" | "instructKey">,
): Promise<string> {
  const key = keys.approveKey ?? keys.instructKey;
  return toHex((await sha256(fromBase64Url(key))).slice(0, 16));
}

const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

/** Crockford base32 (5 bits a character, the last one padded with zeros). */
export function toBase32(bytes: Uint8Array): string {
  let bits = 0;
  let value = 0;
  let out = "";
  for (const byte of bytes) {
    value = ((value << 8) | byte) & 0xffff;
    bits += 8;
    while (bits >= 5) {
      out += CROCKFORD[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }
  if (bits > 0) out += CROCKFORD[(value << (5 - bits)) & 31];
  return out;
}

/** null when a character is not Crockford base32 (O, I and L are read as 0, 1 and 1). */
export function fromBase32(text: string): Uint8Array | null {
  const clean = text
    .toUpperCase()
    .replace(/[\s-]/g, "")
    .replace(/O/g, "0")
    .replace(/[IL]/g, "1");
  const bytes: number[] = [];
  let bits = 0;
  let value = 0;
  for (const ch of clean) {
    const v = CROCKFORD.indexOf(ch);
    if (v < 0) return null;
    value = ((value << 5) | v) & 0xffff;
    bits += 5;
    if (bits >= 8) {
      bytes.push((value >>> (bits - 8)) & 255);
      bits -= 8;
    }
  }
  if (clean.length !== Math.ceil((bytes.length * 8) / 5)) return null;
  return new Uint8Array(bytes);
}

const groups = (text: string) => text.match(/.{1,4}/g)?.join("-") ?? text;

/**
 * What a person compares between two screens: the SHA-256 of an approve key,
 * first 10 bytes, as 16 characters of Crockford base32 in groups of 4.
 */
export async function fingerprintOf(approveKey: string): Promise<string> {
  return groups(
    toBase32((await sha256(fromBase64Url(approveKey))).slice(0, 10)),
  );
}

const ES256 = { name: "ECDSA", namedCurve: "P-256" } as const;
const SIGN = { name: "ECDSA", hash: "SHA-256" } as const;

export interface TrustKeyPair {
  /** base64url of the uncompressed point. */
  publicKey: string;
  /** The private key as JWK (kept sealed by whoever holds it). */
  privateKey: JsonWebKey;
}

export async function generateTrustKey(): Promise<TrustKeyPair> {
  const pair = await subtle().generateKey(ES256, true, ["sign", "verify"]);
  const raw = new Uint8Array(await subtle().exportKey("raw", pair.publicKey));
  const privateKey = await subtle().exportKey("jwk", pair.privateKey);
  return { publicKey: toBase64Url(raw), privateKey };
}

export async function signBytes(
  privateKey: JsonWebKey,
  data: Uint8Array,
): Promise<string> {
  const key = await subtle().importKey(
    "jwk",
    privateKey as never,
    ES256,
    false,
    ["sign"],
  );
  return toBase64Url(new Uint8Array(await subtle().sign(SIGN, key, ab(data))));
}

export async function verifyBytes(
  publicKey: string,
  data: Uint8Array,
  sig: string,
): Promise<boolean> {
  try {
    const key = await subtle().importKey(
      "raw",
      ab(fromBase64Url(publicKey)),
      ES256,
      false,
      ["verify"],
    );
    return await subtle().verify(SIGN, key, ab(fromBase64Url(sig)), ab(data));
  } catch {
    return false;
  }
}

const unsigned = (entry: TrustEntry): UnsignedTrustEntry => {
  const { sig: _sig, ...rest } = entry;
  return rest;
};

export async function signEntry(
  draft: UnsignedTrustEntry,
  approvePrivateKey: JsonWebKey,
): Promise<TrustEntry> {
  const sig = await signBytes(
    approvePrivateKey,
    utf8(canonicalJson(unsigned(draft as TrustEntry))),
  );
  return { ...draft, sig };
}

export async function entryHash(entry: TrustEntry): Promise<string> {
  return toHex(await sha256(utf8(canonicalJson(entry))));
}

export type ChainProblem =
  | "empty"
  | "shape"
  | "other_list"
  | "gap"
  | "prev"
  | "not_approver"
  | "signature"
  | "member"
  | "last_approver"
  | "rolled_back"
  | "forked";

export class ChainError extends Error {
  constructor(
    readonly problem: ChainProblem,
    readonly n: number | null,
  ) {
    super(`trust chain: ${problem}${n === null ? "" : ` at ${n}`}`);
    this.name = "ChainError";
  }
}

async function checkMember(member: TrustMember, n: number): Promise<void> {
  const wantsApprove = member.role === "approver";
  if (wantsApprove !== (member.approveKey !== null))
    throw new ChainError("member", n);
  if (member.kind === "cloud" && (wantsApprove || member.instructs === "any"))
    throw new ChainError("member", n);
  if ((await memberIdOf(member)) !== member.id)
    throw new ChainError("member", n);
}

/**
 * The state a whole chain comes to, or ChainError. `known`: the head this
 * device accepted before (the chain must hold it, unchanged).
 */
export async function verifyChain(
  entries: readonly TrustEntry[],
  known?: TrustHead | null,
): Promise<TrustState> {
  if (entries.length === 0) throw new ChainError("empty", null);
  let members: TrustMember[] = [];
  let prev: string | null = null;
  const list = entries[0].list;
  let knownSeen = false;
  for (const [i, raw] of entries.entries()) {
    const n = i + 1;
    const parsed = trustEntryShape.safeParse(raw);
    if (!parsed.success) throw new ChainError("shape", n);
    const entry = parsed.data as TrustEntry;
    if (entry.list !== list) throw new ChainError("other_list", n);
    if (entry.n !== n) throw new ChainError("gap", n);
    if (entry.prev !== prev) throw new ChainError("prev", n);
    const change = entry.change;
    let signer: TrustMember | undefined;
    if ("genesis" in change) {
      if (n !== 1) throw new ChainError("gap", n);
      await checkMember(change.genesis, n);
      if (change.genesis.role !== "approver" || entry.by !== change.genesis.id)
        throw new ChainError("not_approver", n);
      signer = change.genesis;
    } else {
      if (n === 1) throw new ChainError("gap", n);
      signer = members.find((m) => m.id === entry.by);
      if (!signer || signer.role !== "approver" || !signer.approveKey)
        throw new ChainError("not_approver", n);
    }
    const ok = await verifyBytes(
      signer.approveKey as string,
      utf8(canonicalJson(unsigned(entry))),
      entry.sig,
    );
    if (!ok) throw new ChainError("signature", n);
    if ("genesis" in change) members = [change.genesis];
    else if ("add" in change) {
      await checkMember(change.add, n);
      if (members.some((m) => m.id === change.add.id))
        throw new ChainError("member", n);
      members = [...members, change.add];
    } else {
      if (!members.some((m) => m.id === change.remove))
        throw new ChainError("member", n);
      const rest = members.filter((m) => m.id !== change.remove);
      if (!rest.some((m) => m.role === "approver"))
        throw new ChainError("last_approver", n);
      members = rest;
    }
    prev = await entryHash(entry);
    if (known && known.list === list && known.n === n) {
      if (known.hash !== prev) throw new ChainError("forked", n);
      knownSeen = true;
    }
  }
  if (known && known.list === list && !knownSeen)
    throw new ChainError("rolled_back", entries.length);
  return { list, n: entries.length, hash: prev as string, members };
}

/** The next entry's draft on top of a state (sign it with `by`'s approve key). */
export function nextEntry(
  state: TrustState | null,
  change: TrustChange,
  by: string,
  at = new Date().toISOString(),
): UnsignedTrustEntry {
  return {
    v: TRUST_VERSION,
    list: state?.list ?? newListId(),
    n: state ? state.n + 1 : 1,
    prev: state?.hash ?? null,
    change,
    by,
    at,
  };
}

// ---- Approval handshake ----------------------------------------------------

/** Where the two devices meet: the Vault both use, or the Cloud account both are signed in to. */
export type ApprovalPlace = "vault" | "cloud";
const PLACES: readonly ApprovalPlace[] = ["vault", "cloud"];

export interface ApprovalCode {
  secret: Uint8Array;
  place: ApprovalPlace;
}

/** The secret and one byte for the place, as Crockford base32 in groups of 4 (12 characters). */
export function encodeApprovalCode(code: ApprovalCode): string {
  const bytes = new Uint8Array(APPROVAL_SECRET_BYTES + 1);
  bytes.set(code.secret.slice(0, APPROVAL_SECRET_BYTES));
  bytes[APPROVAL_SECRET_BYTES] = PLACES.indexOf(code.place);
  return groups(toBase32(bytes));
}

/** The page an approval QR opens (the app on a phone that has it). */
export const TRUST_APPROVE_URL = "https://trameria.com/trust/approve";

/** What a QR holds: the page's URL, the code without its dashes after `#`. */
export const approvalQrText = (code: string) =>
  `${TRUST_APPROVE_URL}#${code.replace(/-/g, "")}`;

/** null when the text is neither an approval code nor its QR's URL. */
export function decodeApprovalCode(text: string): ApprovalCode | null {
  let given = text.trim();
  if (given.startsWith(TRUST_APPROVE_URL)) {
    const at = given.indexOf("#");
    given = at < 0 ? "" : given.slice(at + 1);
  }
  const bytes = fromBase32(given);
  if (!bytes || bytes.length !== APPROVAL_SECRET_BYTES + 1) return null;
  const place = PLACES[bytes[APPROVAL_SECRET_BYTES]];
  if (!place) return null;
  return { secret: bytes.slice(0, APPROVAL_SECRET_BYTES), place };
}

export const newApprovalCode = (place: ApprovalPlace): ApprovalCode => ({
  secret: randomBytes(APPROVAL_SECRET_BYTES),
  place,
});

export interface ApprovalKeys {
  /** The room's name at the store (hex, 32 characters). */
  room: string;
  /** The HMAC key both sides prove the secret with. */
  macKey: Uint8Array;
}

/** What the code's secret gives: slow on purpose (PBKDF2), so a room's name does not give the code back. */
export async function approvalKeys(secret: Uint8Array): Promise<ApprovalKeys> {
  const base = await subtle().importKey("raw", ab(secret), "PBKDF2", false, [
    "deriveBits",
  ]);
  const bits = new Uint8Array(
    await subtle().deriveBits(
      {
        name: "PBKDF2",
        hash: "SHA-256",
        salt: ab(utf8(APPROVAL_KDF_SALT)),
        iterations: APPROVAL_KDF_ITERATIONS,
      },
      base,
      384,
    ),
  );
  return { room: toHex(bits.slice(0, 16)), macKey: bits.slice(16, 48) };
}

/** The new device's first message: who it is. */
export interface ApprovalHello {
  t: "hello";
  member: TrustMember;
  r: string;
}

/** The approver's answer: who it is, the list it adds to, and its proof. */
export interface ApprovalOffer {
  t: "offer";
  member: TrustMember;
  list: string;
  r: string;
  mac: string;
}

export type ApprovalRole = "approver" | "new";

async function hmac(key: Uint8Array, data: Uint8Array): Promise<string> {
  const k = await subtle().importKey(
    "raw",
    ab(key),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  return toBase64Url(new Uint8Array(await subtle().sign("HMAC", k, ab(data))));
}

/** One side's proof over both sides' messages (the offer without its mac). */
export function approvalMac(
  macKey: Uint8Array,
  role: ApprovalRole,
  hello: ApprovalHello,
  offer: Omit<ApprovalOffer, "mac">,
): Promise<string> {
  const { mac: _mac, ...said } = offer as ApprovalOffer;
  return hmac(macKey, utf8(canonicalJson({ role, hello, offer: said })));
}

/**
 * A Cloud account handed to the new device along with the list: the approver,
 * signed in there, asked Cloud for a one-time ticket for the new member. It is
 * Cloud's credential, never a way into the list.
 */
export interface CloudHandoff {
  /** The cloud's API origin. */
  url: string;
  ticket: string;
  account: { id: string; name: string };
}

/** The approver's proof that the handoff is its own (a store cannot swap in another account). */
export function handoffMac(
  macKey: Uint8Array,
  hello: ApprovalHello,
  offer: Omit<ApprovalOffer, "mac">,
  handoff: CloudHandoff,
): Promise<string> {
  const { mac: _mac, ...said } = offer as ApprovalOffer;
  return hmac(
    macKey,
    utf8(canonicalJson({ role: "handoff", hello, offer: said, handoff })),
  );
}

/** How far the redeeming device's clock may be from Cloud's. */
export const HANDOFF_SKEW_MS = 10 * 60 * 1000;

/**
 * What the new device signs with its instruct key to redeem a ticket (Cloud
 * checks it with the instruct key the list names): only that device can.
 */
export const handoffProofBytes = (ticket: string, at: string) =>
  utf8(canonicalJson({ t: "trameria cloud handoff v1", ticket, at }));

/**
 * What a device signed in to Cloud some other way (a device code) signs with
 * its instruct key to tell Cloud which member of the list that sign-in is
 * (`hub`: its id there), so a removal from the list ends it too.
 */
export const memberProofBytes = (hub: string, at: string) =>
  utf8(canonicalJson({ t: "trameria cloud member v1", hub, at }));

/** Compares two proofs without telling how much of them matched. */
export function sameProof(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}
